/** The one window vocabulary the dashboard's notes and caregiver-load panels share. */
export type DashboardPeriod = 'today' | 'week' | '30d';

export type DashboardPeriodWindow = {
  /** Inclusive: the first instant inside the window. */
  from: string;
  /** Exclusive: the first instant after the window. */
  to: string;
};

/** The default the page opens on: a single day's assigned hours swing too much to read. */
export const DEFAULT_DASHBOARD_PERIOD: DashboardPeriod = 'week';

/** One label per period, shared by the page's control and the caregiver drawer's captions. */
export const DASHBOARD_PERIOD_LABEL_KEYS: Record<DashboardPeriod, string> = {
  today: 'dashboard-period-today',
  week: 'dashboard-period-week',
  '30d': 'dashboard-period-30d',
};

/** Left to right, shortest window first. */
export const DASHBOARD_PERIOD_ORDER: DashboardPeriod[] = ['today', 'week', '30d'];

/** `Date.getDay()` numbering: 0 = Sunday, 1 = Monday. */
const MONDAY = 1;
const DAYS_IN_WEEK = 7;

/** 30 local days *including* today, so the window starts 29 days back — not 30, which is 31 days. */
const DAYS_BACK_30D = 29;

/**
 * The window for a period, as absolute instants, in whatever timezone the browser is in.
 *
 * - `today` — local midnight → the next local midnight.
 * - `week` — Monday 00:00 local → the *following* Monday 00:00 local.
 * - `30d` — local midnight 29 days back → the next local midnight, i.e. 30 local days ending
 *   today. "30 days back → next midnight" is 31 days; "30 days back → today's midnight" hides
 *   today.
 *
 * **Half-open: `[from, to)`.** The workload endpoint filters on `v.start_time < period_to` and
 * clamps with `LEAST(v.end_time, period_to)`, so the end is the first instant *outside* the
 * window. A `23:59:59.999` end describes a window a millisecond short of the period.
 *
 * Monday is stated explicitly rather than taken from dayjs `.startOf('week')`, whose first day
 * of week comes from the active locale: the same code would silently produce a Sunday-start
 * week for a user on a Sunday-first locale and shift every caregiver's hours by a day.
 *
 * Both ends move by whole *calendar* days and are then re-anchored to local midnight, never by
 * a fixed count of milliseconds — adding 7 × 24 h to a Monday midnight lands an hour into the
 * next Monday in the week a zone springs forward.
 *
 * No timezone is named anywhere: `Date`'s local getters and setters are the user's zone, and
 * `toISOString()` converts to the absolute instant the API wants.
 */
export const getDashboardPeriodWindow = (
  period: DashboardPeriod,
  reference: Date = new Date()
): DashboardPeriodWindow => {
  const start = new Date(reference.getTime());

  if (period === 'week') {
    // Sunday (0) is the *last* day of the week here, so it maps to 6 days after Monday.
    const daysSinceMonday = (start.getDay() - MONDAY + DAYS_IN_WEEK) % DAYS_IN_WEEK;
    start.setDate(start.getDate() - daysSinceMonday);
  } else if (period === '30d') {
    start.setDate(start.getDate() - DAYS_BACK_30D);
  }

  start.setHours(0, 0, 0, 0);

  // `today` and `30d` both end at the midnight after the reference day; only `week` counts its
  // days forward from its own Monday.
  const isWeek = period === 'week';
  const end = new Date((isWeek ? start : reference).getTime());
  end.setDate(end.getDate() + (isWeek ? DAYS_IN_WEEK : 1));
  // Re-anchor: `setDate` keeps the wall-clock time, which is midnight for the week, but a zone
  // whose DST change happens *at* midnight has no 00:00 on that date and would land at 01:00.
  end.setHours(0, 0, 0, 0);

  return { from: start.toISOString(), to: end.toISOString() };
};
